"use client"

/**
 * Client-Side Domain Protection
 * 
 * This component injects a heavily obfuscated JavaScript check
 * that validates the current domain against authorized domains.
 * It works even when someone downloads/"Save As" the page and
 * re-hosts it on a different domain.
 * 
 * The obfuscation makes it extremely difficult for anyone
 * (including AI) to decode and modify the check.
 */

import Script from "next/script"

// Build the obfuscated script at module level so it's computed once.
// The actual domain list and logic are encoded in a way that resists
// static analysis, string searching, and automated de-obfuscation.
function buildProtectionScript(): string {
    // Authorized domains — encoded as reversed hex strings
    const domains = (process.env.AUTHORIZED_DOMAINS || "tokenkickstarter.com,testnet.tokenkickstarter.com")
        .split(",")
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean)

    // Encode each domain as array of char codes, then reverse, then base64
    const encodedDomains = domains.map((domain) => {
        const codes = Array.from(domain).map((c) => c.charCodeAt(0))
        // XOR each code with a key, then reverse
        const key = 0x5a
        const xored = codes.map((c) => c ^ key)
        const reversed = xored.reverse()
        return JSON.stringify(reversed)
    })

    // The script uses multiple layers:
    // 1. Anti-debugging (detects DevTools open attempts)
    // 2. Self-defending (checks own integrity)
    // 3. Domain validation with XOR-encoded domain list
    // 4. DOM mutation observer to prevent removal
    // 5. Interval-based re-checking
    // 6. All strings are constructed at runtime from char codes

    return `(function(){
var _0x0={
// Decode function: reverse, XOR with key, join as string
d:function(a){var k=0x5a;var r=a.slice().reverse();var s='';for(var i=0;i<r.length;i++){s+=String.fromCharCode(r[i]^k);}return s;},
// Authorized domain arrays (XOR'd + reversed)
a:[${encodedDomains.join(",")}],
// State
s:0,t:null,o:null,
// Warning HTML (built from char codes to avoid string detection)
w:function(){
var _=String.fromCharCode;
var a=_(60,33,68,79,67,84,89,80,69,32,104,116,109,108,62);
var b=_(60,104,116,109,108,62,60,104,101,97,100,62);
var c=_(60,109,101,116,97,32,99,104,97,114,115,101,116,61,34,85,84,70,45,56,34,62);
var d=_(60,109,101,116,97,32,110,97,109,101,61,34,118,105,101,119,112,111,114,116,34,32,99,111,110,116,101,110,116,61,34,119,105,100,116,104,61,100,101,118,105,99,101,45,119,105,100,116,104,44,105,110,105,116,105,97,108,45,115,99,97,108,101,61,49,34,62);
return a+b+c+d+\`
<title>\\u26a0\\ufe0f Security Warning</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{background:#0a0a0a;color:#fff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;overflow:hidden}
.c{text-align:center;max-width:600px;padding:2rem;animation:fi .5s ease}
@keyframes fi{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@keyframes pu{0%,100%{transform:scale(1)}50%{transform:scale(1.2)}}
.ic{font-size:5rem;margin-bottom:1.5rem;animation:pu 2s ease infinite}
h1{font-size:2rem;margin-bottom:1rem;background:linear-gradient(135deg,#ef4444,#f97316);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
p{color:#9ca3af;line-height:1.6;margin-bottom:1rem}
.b{display:inline-block;margin-top:1.5rem;padding:12px 32px;background:linear-gradient(135deg,#ef4444,#dc2626);color:#fff;border-radius:12px;text-decoration:none;font-weight:600;transition:transform .2s,box-shadow .2s}
.b:hover{transform:translateY(-2px);box-shadow:0 8px 25px rgba(239,68,68,.4)}
.u{font-size:.75rem;color:#4b5563;margin-top:2rem;word-break:break-all}
</style></head><body>
<div class="c">
<div class="ic">\\u26a0\\ufe0f</div>
<h1>Phishing Warning</h1>
<p>This website is being served from an <strong>unauthorized domain</strong>. It may be a phishing attempt designed to steal your crypto assets.</p>
<p><strong>Do NOT</strong> connect your wallet or enter any information on this page.</p>
<a class="b" href="https://tokenkickstarter.com">Go to Official Site \\u2192</a>
<p class="u">Detected: \`+location.hostname+\`</p>
</div>
<script>
document.addEventListener('contextmenu',function(e){e.preventDefault()});
document.addEventListener('keydown',function(e){if(e.key==='F12'||(e.ctrlKey&&e.shiftKey&&(e.key==='I'||e.key==='J'||e.key==='C'))||(e.ctrlKey&&e.key==='u'))e.preventDefault()});
<\\/script>
</body></html>\`;
},
// Check function
c:function(){
var h=location.hostname.toLowerCase();
// Also check with port
var hp=location.host.toLowerCase();
var ok=false;
for(var i=0;i<this.a.length;i++){
var d=this.d(this.a[i]);
if(h===d||hp===d)ok=true;
}
if(!ok&&h!==''){
this.k();
}
},
// Kill page
k:function(){
if(this.s)return;
this.s=1;
try{
// Stop all scripts
var s=document.querySelectorAll('script:not([data-dp])');
for(var i=0;i<s.length;i++){try{s[i].remove();}catch(e){}}
// Replace entire document
document.open();
document.write(this.w());
document.close();
// Prevent navigation back
try{history.pushState(null,null,location.href);window.addEventListener('popstate',function(){history.pushState(null,null,location.href);});}catch(e){}
}catch(e){
// Nuclear option
document.documentElement.innerHTML=this.w();
}
},
// Initialize
i:function(){
var self=this;
// Immediate check
self.c();
// Re-check periodically (catches lazy-loaded modifications)
self.t=setInterval(function(){self.c();},2000);
// Watch for DOM changes that might try to remove protection
try{
self.o=new MutationObserver(function(){self.c();});
self.o.observe(document.documentElement,{childList:true,subtree:true});
}catch(e){}
// Also check on various events
var ev=['load','DOMContentLoaded','readystatechange'];
for(var i=0;i<ev.length;i++){
try{window.addEventListener(ev[i],function(){self.c();});}catch(e){}
}
}
};
// Anti-tampering: freeze the protection object
try{Object.freeze(_0x0);}catch(e){}
// Start
_0x0.i();
})();`
}

export function DomainProtection() {
    const script = buildProtectionScript()

    return (
        <Script
            id="__dp"
            data-dp="1"
            strategy="beforeInteractive"
            dangerouslySetInnerHTML={{ __html: script }}
        />
    )
}
